// Schedule: one row per project, its bar from the start date (projects.starts_on,
// from the Zoho deal) to the deadline, carrying the lead, and its work items'
// due dates as diamonds: native ones and Jira's (owner, 2026-10-14; spec
// §6.8). Drawn as the Claude Design's GanttScreen (screens2.jsx): Days or
// Weeks, a 7-60 day or 4-20 week window, at-risk and blocked flags, finished
// projects dimmed and struck through. Never a guessed date: a project missing
// one says so. Today and the working week are the org's (org_today,
// org_settings.workdays). What a viewer sees is RLS's answer (projects_read,
// tasks_read). ?person=<id> (Talent's Schedule button) keeps only the projects
// that person is a member of, as far as members_read lets the viewer see.
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { canOpen } from '../lib/roles';
import { useOrgDates } from '../lib/orgSettings';
import { fmtDate, fmtDayRange, isoDay, orgToday, weekStart } from '../lib/dates';
import { place, windowEnd, xOf, type TimeWindow, type Unit } from '../lib/timeline';
import { Card } from '../components/card';
import { GanttRow } from '../components/domain';
import { TimeGrid, TimeHeader, WindowControls, WindowNote, toolbar } from '../components/timeScale';

type Project = {
  id: string;
  name: string;
  client: string | null;
  code: string | null;
  status: string;
  health: string | null;
  tone: string;
  starts_on: string | null;
  deadline: string | null;
  lead_profile: { full_name: string | null } | null;
};
type Due = { project_id: string; code: string | null; title: string; due_date: string };

// A project's colour is a category, so the status hues (sage done, amber
// waiting, rose wrong) are never one: a project stored with one draws in iris,
// the column default.
const CATEGORY = new Set(['iris', 'sky', 'lilac', 'coral', 'cyan']);

// 240px: GanttRow's label column.
const COLUMNS = '240px 1fr';

export function GanttScreen() {
  const { perms } = useAuth();
  const { tz, weekStartsOn, workdays, dateFormat } = useOrgDates();
  const [unit, setUnit] = useState<Unit>('days');
  const [range, setRange] = useState<Record<Unit, number>>({ days: 30, weeks: 12 });
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [dues, setDues] = useState<Due[]>([]);
  const [failed, setFailed] = useState(false);
  const person = useSearchParams()[0].get('person');
  const [theirs, setTheirs] = useState<{ name: string | null; ids: Set<string> } | null>(null);

  const today = isoDay(orgToday(tz));
  const w: TimeWindow = {
    from: isoDay(weekStart(weekStartsOn, orgToday(tz))),
    unit,
    cols: range[unit],
  };
  const { from } = w;
  const to = windowEnd(w);

  useEffect(() => {
    if (!supabase || !person) return;
    const sb = supabase;
    let live = true;
    void Promise.all([
      sb.from('project_members').select('project_id').eq('user_id', person),
      sb.from('profiles').select('full_name').eq('id', person),
    ]).then(([m, p]) => {
      if (!live) return;
      if (m.error) setFailed(true);
      setTheirs({
        name: (p.data as { full_name: string | null }[] | null)?.[0]?.full_name ?? null,
        ids: new Set(((m.data as { project_id: string }[] | null) ?? []).map((r) => r.project_id)),
      });
    });
    return () => {
      live = false;
    };
  }, [person]);

  useEffect(() => {
    if (!supabase) return;
    let live = true;
    void supabase
      .from('projects')
      .select(
        // code:key: a project's shown ID is its key (20261016090000).
        'id, name, client, code:key, status, health, tone, starts_on, deadline, lead_profile:profiles!projects_lead_fkey(full_name)',
      )
      .then(({ data, error }) => {
        if (!live) return;
        if (error) setFailed(true);
        setProjects((data as unknown as Project[]) ?? []);
      });
    return () => {
      live = false;
    };
  }, []);

  // Work items due in the window, the project's own and Jira's alike; a Jira
  // issue gone from Jira is history, not a date.
  useEffect(() => {
    if (!supabase) return;
    let live = true;
    void supabase
      .from('tasks')
      .select('project_id, code, title, due_date')
      .is('jira_gone_at', null)
      .gte('due_date', from)
      .lte('due_date', to)
      .then(({ data, error }) => {
        if (!live) return;
        if (error) setFailed(true);
        setDues((data as Due[]) ?? []);
      });
    return () => {
      live = false;
    };
  }, [from, to]);

  const rows = (projects ?? [])
    .filter((p) => !person || theirs?.ids.has(p.id))
    .filter((p) => !(p.deadline && p.deadline < from) && !(p.starts_on && p.starts_on > to))
    .sort(
      (a, b) =>
        (a.starts_on ?? '9999').localeCompare(b.starts_on ?? '9999') ||
        a.name.localeCompare(b.name),
    );

  if (!supabase) {
    return (
      <div style={{ padding: 40, color: 'var(--hf-ink-3)' }}>
        Supabase isn’t configured, so there is no schedule to show.
      </div>
    );
  }

  const background = <TimeGrid w={w} today={today} workdays={workdays} />;
  // Due on its day: the diamond sits in the middle of it.
  const where = (key: string) => xOf(key, w) + 0.5 / (w.cols * (unit === 'days' ? 1 : 7));

  return (
    <div style={{ padding: '20px 24px' }}>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={toolbar}>
          <WindowControls
            unit={unit}
            cols={w.cols}
            onUnit={setUnit}
            onCols={(r) => setRange((v) => ({ ...v, [unit]: r }))}
          />
          {person && (
            <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-2)' }}>
              {theirs?.name ?? 'One person'}’s projects ·{' '}
              <Link to="/gantt" style={{ color: 'var(--hf-accent-text)' }}>
                Show all
              </Link>
            </span>
          )}
          <div style={{ flex: 1 }} />
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 'var(--hf-text-2xs)',
              color: 'var(--hf-ink-3)',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
              <polygon points="5,0 10,5 5,10 0,5" fill="var(--hf-ink-3)" />
            </svg>
            Work item due
          </span>
          <span style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' }}>
            {fmtDayRange(from, to, today)}
          </span>
          <WindowNote w={w} />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 760 }}>
            <TimeHeader w={w} today={today} workdays={workdays} columns={COLUMNS}>
              <span
                style={{
                  alignSelf: 'center',
                  fontSize: 'var(--hf-text-2xs)',
                  color: 'var(--hf-ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Project
              </span>
            </TimeHeader>

            {projects === null || (person && !theirs) ? (
              <div style={{ padding: 24, color: 'var(--hf-ink-3)' }}>Loading…</div>
            ) : failed ? (
              <div role="alert" style={{ padding: 24, color: 'var(--hf-rose-text)' }}>
                The schedule couldn’t load. Refresh to try again.
              </div>
            ) : rows.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--hf-ink-3)' }}>
                No projects you can see run in this window.
              </div>
            ) : (
              rows.map((p) => {
                const done = p.status === 'done';
                const fmt = (k: string) => fmtDate(k, dateFormat);
                const { starts_on: start, deadline: end } = p;
                const bar = start && end && start <= end ? place({ start, end }, w) : null;
                const note = bar
                  ? undefined
                  : !start
                    ? end
                      ? `No start date · deadline ${fmt(end)}`
                      : 'No start date'
                    : !end
                      ? `No deadline · starts ${fmt(start)}`
                      : `Deadline ${fmt(end)} is before the start ${fmt(start)}`;
                return (
                  <GanttRow
                    key={p.id}
                    name={
                      p.client && !p.name.startsWith(p.client) ? `${p.client} · ${p.name}` : p.name
                    }
                    code={p.code ?? p.id.slice(0, 8)}
                    tone={CATEGORY.has(p.tone) ? p.tone : 'iris'}
                    done={done}
                    flag={
                      done
                        ? null
                        : p.status === 'blocked' || p.health === 'blocked'
                          ? 'blocked'
                          : p.status === 'risk' || p.health === 'at-risk'
                            ? 'at-risk'
                            : null
                    }
                    lead={p.lead_profile?.full_name ?? null}
                    bar={bar}
                    note={note}
                    markers={dues
                      .filter((t) => t.project_id === p.id)
                      .map((t) => ({
                        at: where(t.due_date),
                        label: `${t.code ?? 'Work item'} ${t.title}, due ${fmt(t.due_date)}`,
                      }))}
                    background={background}
                    href={canOpen(perms, 'projects') ? `/project?id=${p.id}` : undefined}
                  />
                );
              })
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
